import type { AxiosInstance } from "axios";

import { toast } from "sonner";

import type { Order } from "@/types/order";

import useAuthStore from "@/store/auth";
import { usePaystackStore } from "@/store/paystack";
import { useSafeHaven } from "@/store/safehaven";
import { useVPaymentWidget } from "@/store/vfd";
import { PaymentProvider } from "@/types/banks";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { usePrivateAxios } from "./use-private-axios";
import { useVerifyVfdSession } from "./wallet-query";

interface Payment {
	orderAmount: number;
	contractId: string;
	paymentOption: PaymentProvider;
	courierPickupOfficeId: string;
}

export interface PaymentConfig {
	publicKey: string;
	clientId: string;
	reference?: string;
	accessToken?: string;
	merchantId?: string;
	settlementAccount: {
		bankCode: string;
		accountNumber: string;
		accountName?: string;
	};
}

interface InitiatePaymentResponse {
	data: {
		order: Order;
		paymentConfig: PaymentConfig;
	};
}
async function initPayment(privateAxios: AxiosInstance, payment: Payment) {
	const { data } = await privateAxios.post<InitiatePaymentResponse>("/order/initiate-payment", payment);
	return data.data;
}
interface VerifyPayement {
	reference: string;
	paymentProvider: string;
}
async function fetchVerifyPayment(privateAxios: AxiosInstance, payment: VerifyPayement) {
	const { data } = await privateAxios.post("/order/verify-payment", payment);
	return data;
}

export function useVerifyPayment() {
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationKey: ["verify-payments"],
		mutationFn: (payment: VerifyPayement) => fetchVerifyPayment(privateAxios, payment),
		onError: () => toast.error("Payment failed"),
		onSuccess: () => toast.success("Payment successful")
	});
}

export function usePaymentTransaction() {
	const queryClient = useQueryClient();
	const safeHaven = useSafeHaven();
	const paystack = usePaystackStore();
	const privateAxios = usePrivateAxios();
	const verifyPayment = useVerifyPayment();
	const vfd = useVPaymentWidget();

	const verifyVfdSession = useVerifyVfdSession();

	const user = useAuthStore(state => state.user);

	const init = useMutation({
		mutationKey: ["payments"],
		mutationFn: async (payment: Payment & { newRef?: boolean }) => {
			return initPayment(privateAxios, payment);
		},
		onSettled(res, _, variables) {
			if (res && variables.paymentOption === PaymentProvider.VFD) {
				localStorage.setItem("vfd-session", res.paymentConfig.accessToken ?? "");
			}
		}
	});

	const processPayment = async (payment: Payment) => {
		if (!init.data || !init.isSuccess)
			throw new Error("Payment not initialized");
		switch (payment.paymentOption) {
			case PaymentProvider.PAYSTACK:
				paystack.initializePayment({
					amount: payment.orderAmount * 100,
					email: user?.email ?? "",
					reference: init.data?.order.paymentRef ?? "",
					key: init.data?.paymentConfig.publicKey ?? "",
					metadata: {
						custom_fields: [
							{
								display_name: user?.fullName ?? "",
								value: user?.fullName ?? "",
								variable_name: user?.fullName ?? ""
							}
						]
					},
					onSuccess: async () => {
						await verifyPayment.mutateAsync({
							paymentProvider: payment.paymentOption,
							reference: init.data?.order.paymentRef ?? ""
						});
						queryClient.invalidateQueries({ queryKey: ["view-wallet"] });
					},
					onError() {
						toast.error("Payment failed");
					},
					onCancel() {
						toast.warning("Payment cancelled");
					}
				});
				break;
			case PaymentProvider.SAFEHAVEN:
				if (!user)
					throw new Error("User not found");
				safeHaven.initializePayment({
					customer: {
						firstName: user.firstName,
						lastName: user.lastName,
						emailAddress: user.email,
						phoneNumber: user.phoneNumber ?? "+234 723 432 3434"
					},
					clientId: init.data?.paymentConfig.clientId ?? "",
					settlementAccount: init.data.paymentConfig.settlementAccount,
					amount: payment.orderAmount,
					referenceCode: init.data?.order.paymentRef ?? "",
					callback() {
						verifyPayment.mutateAsync({
							paymentProvider: payment.paymentOption,
							reference: init.data?.order.paymentRef ?? ""
						}).then(() => {
							queryClient.invalidateQueries({ queryKey: ["view-wallet"] });
						});
					}
				});
				queryClient.invalidateQueries({ queryKey: ["view-wallet"] });
				break;

			case PaymentProvider.VFD:
				vfd.initializePayment({
					amount: payment.orderAmount,
					token: init.data?.paymentConfig.accessToken ?? "",
					merchantId: init.data?.paymentConfig.merchantId ?? "",
					onSuccess: async (res) => {
						await verifyVfdSession.mutateAsync({
							transactionId: init.data.order.id,
							sessionId: res.reference,
							type: "ORDER"
						});
						verifyPayment.mutateAsync({
							paymentProvider: payment.paymentOption,
							reference: init.data.order.paymentRef ?? ""
						});
						queryClient.invalidateQueries({ queryKey: ["view-wallet"] });
					},
					onClose() {
						toast.warning("Payment cancelled");
					}
				});
				break;
			case PaymentProvider.WALLET:
				verifyPayment.mutateAsync({
					paymentProvider: payment.paymentOption,
					reference: init.data?.order.paymentRef ?? ""
				}).then(() => {
					if (verifyPayment.isSuccess)
						toast.success("Payment successful");
					else
						toast.error("Payment failed");
				}).catch((error) => {
					toast.error("Payment failed", { description: error.response.data.message });
				}).finally(() => {
					queryClient.invalidateQueries({ queryKey: ["view-wallet"] });
				});
				break;
			default:
				throw new Error("Invalid payment provider");
		}
	};

	return {
		paystack,
		tranxInit: init,
		processPayment,
		verifyPayment
	};
}
